<template>
  <main class="user-admin-page">
    <section v-if="$canView('administrador', 'superadministrador')" class="user-admin-shell">
      <header class="page-head">
        <div>
          <p>JULIAM ARCADE · CONTROL DE ACCESO</p>
          <h1>Usuarios y claves</h1>
          <span>Crea cuentas o genera una invitación con rol exacto.</span>
        </div>
        <button type="button" class="ghost-button" @click="goBack">← Volver</button>
      </header>

      <div class="admin-panels">
        <article class="glass-panel" data-tone="cyan">
          <div class="panel-number">01</div>
          <h2>Crear cuenta</h2>
          <p class="panel-copy">La cuenta se asignará exclusivamente a Juliam Arcade.</p>

          <form class="admin-form" @submit.prevent="handleRegister">
            <label>
              <span>Nombre</span>
              <input v-model.trim="nombre" type="text" placeholder="Nombre y apellido" required>
            </label>
            <label>
              <span>Correo</span>
              <input v-model.trim="email" type="email" placeholder="nombre@correo.com" required>
            </label>
            <label>
              <span>Teléfono</span>
              <input :value="telefono" type="tel" inputmode="numeric" maxlength="10" placeholder="10 dígitos" required @input="onPhoneInput">
            </label>
            <div class="form-pair">
              <label>
                <span>Contraseña</span>
                <input v-model="password" type="password" minlength="8" placeholder="Mínimo 8 caracteres" required>
              </label>
              <label>
                <span>Confirmar</span>
                <input v-model="confirmPassword" type="password" minlength="8" required>
              </label>
            </div>
            <label>
              <span>Rol exacto</span>
              <select v-model="role" required>
                <option value="" disabled>Selecciona un rol</option>
                <option v-for="option in assignableRoles" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </label>
            <button class="primary-button" type="submit" :disabled="savingUser">
              {{ savingUser ? 'Creando...' : 'Crear usuario' }}
            </button>
          </form>
        </article>

        <article class="glass-panel" data-tone="pink">
          <div class="panel-number">02</div>
          <h2>Claves de registro</h2>
          <p class="panel-copy">Cada clave pertenece a Juliam y se consume al completar el registro.</p>

          <form class="key-form" @submit.prevent="handleCreateKey">
            <label>
              <span>Rol de la invitación</span>
              <select v-model="selectedRoleKey" required>
                <option value="" disabled>Selecciona un rol</option>
                <option v-for="option in assignableRoles" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </label>
            <button class="primary-button pink" type="submit" :disabled="savingKey">
              {{ savingKey ? 'Generando...' : 'Generar clave' }}
            </button>
          </form>

          <div v-if="keys.length" class="key-list">
            <div v-for="key in keys" :key="key.id_token_clave" class="key-row">
              <a :href="registrationLink(key.id_token_clave)" target="_blank" rel="noopener noreferrer">
                <small>CLAVE</small>
                <strong>{{ key.id_token_clave }}</strong>
              </a>
              <span>{{ roleLabel(key.rol) }}</span>
              <button type="button" @click="deleteKey(key.id_token_clave)">Eliminar</button>
            </div>
          </div>
          <p v-else class="empty-state">Todavía no hay claves activas.</p>
        </article>
      </div>

      <ModalConfirm :visible="modalVisible" :link="link" message="Comparte esta liga con la persona que se registrará." @close="closeModal" />
    </section>

    <section v-else class="access-denied">
      <strong>403</strong>
      <p>Tu rol no tiene acceso a la administración de usuarios.</p>
      <router-link to="/dashboard">Volver al panel</router-link>
    </section>
  </main>
</template>

<script>
import axios from 'axios'
import ModalConfirm from '@/components/modal/ModalConfirm.vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'AgregarUsuario',
  components: { ModalConfirm },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      nombre: '', email: '', telefono: '', password: '', confirmPassword: '', role: 'usuario',
      keys: [], selectedRoleKey: 'usuario', savingUser: false, savingKey: false,
      modalVisible: false, link: ''
    }
  },
  computed: {
    currentRole() { return this.$store.state.role },
    userToken() { return this.$store.state.user?.token || '' },
    assignableRoles() {
      if (this.currentRole === 'superadministrador') return [
        { value: 'superadministrador', label: 'Superadministrador' },
        { value: 'administrador', label: 'Administrador' },
        { value: 'usuario', label: 'Usuario' }
      ]
      return [
        { value: 'administrador', label: 'Administrador' },
        { value: 'usuario', label: 'Usuario' }
      ]
    },
    authHeaders() {
      return {
        Authorization: `Bearer ${this.userToken}`,
        'X-User-Role': this.currentRole,
        'X-User-Token': this.userToken
      }
    }
  },
  async created() { await this.fetchKeys() },
  methods: {
    onPhoneInput(event) { this.telefono = String(event.target.value || '').replace(/\D/g, '').slice(0, 10) },
    validateEmail(value) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) },
    async handleRegister() {
      if (!this.validateEmail(this.email)) return this.toast.warning('Introduce un correo válido.')
      if (this.telefono.length !== 10) return this.toast.warning('El teléfono debe tener 10 dígitos.')
      if (this.password.length < 8) return this.toast.warning('La contraseña debe tener al menos 8 caracteres.')
      if (this.password !== this.confirmPassword) return this.toast.error('Las contraseñas no coinciden.')

      this.savingUser = true
      try {
        await axios.post('/api/register', {
          nombre: this.nombre,
          email: this.email,
          telefono: this.telefono,
          password: this.password,
          role: this.role,
          groupId: 'JULIAM01'
        }, { headers: this.authHeaders })
        this.toast.success('Usuario creado y wallet preparada.')
        this.nombre = ''; this.email = ''; this.telefono = ''; this.password = ''; this.confirmPassword = ''; this.role = 'usuario'
      } catch (error) {
        this.toast.error(error.response?.data?.error || 'No se pudo crear el usuario.')
      } finally {
        this.savingUser = false
      }
    },
    async handleCreateKey() {
      this.savingKey = true
      try {
        const { data } = await axios.post('/api/create-key', { role: this.selectedRoleKey }, { headers: this.authHeaders })
        if (!data.success) throw new Error(data.message || 'No se pudo generar la clave.')
        this.keys.unshift(data.key)
        this.link = data.key.link || this.registrationLink(data.key.id_token_clave)
        this.modalVisible = true
        this.toast.success('Clave generada.')
      } catch (error) {
        this.toast.error(error.response?.data?.message || error.message || 'No se pudo generar la clave.')
      } finally {
        this.savingKey = false
      }
    },
    async fetchKeys() {
      try {
        const { data } = await axios.get('/api/keys', { headers: this.authHeaders })
        this.keys = data.keys || []
      } catch (error) {
        if (error.response?.status !== 403) this.toast.error('No se pudieron cargar las claves.')
      }
    },
    async deleteKey(token) {
      try {
        await axios.post('/api/delete-key', { token }, { headers: this.authHeaders })
        this.keys = this.keys.filter(key => key.id_token_clave !== token)
        this.toast.success('Clave eliminada.')
      } catch (error) {
        this.toast.error(error.response?.data?.message || 'No se pudo eliminar la clave.')
      }
    },
    registrationLink(token) { return `https://juliam.gestiontotal.app/registrarse/${token}` },
    roleLabel(role) { return role === 'superadministrador' ? 'Superadmin' : role === 'administrador' ? 'Admin' : 'Usuario' },
    closeModal() { this.modalVisible = false },
    goBack() { this.$router.back() }
  }
}
</script>

<style scoped>
.user-admin-page { min-height: 100vh; padding: clamp(20px, 4vw, 54px); color: #f7f8ff; background: radial-gradient(circle at 15% 12%, rgba(24, 226, 255, .12), transparent 27%), radial-gradient(circle at 86% 18%, rgba(255, 47, 180, .13), transparent 30%), #050813; }
.user-admin-shell { width: min(1180px, 100%); margin: 0 auto; }
.page-head { display: flex; justify-content: space-between; gap: 24px; align-items: end; margin-bottom: 28px; }
.page-head p { margin: 0 0 8px; color: #72f5ff; font-size: 12px; font-weight: 900; letter-spacing: .18em; }
.page-head h1 { margin: 0; font-family: var(--juliam-display); font-size: clamp(38px, 6vw, 68px); text-transform: uppercase; letter-spacing: -.03em; }
.page-head span { color: #aeb5cc; }
.ghost-button, .primary-button { border: 1px solid rgba(114, 245, 255, .48); color: #f7f8ff; background: rgba(8, 14, 34, .78); padding: 13px 18px; font: 900 12px/1 var(--juliam-mono); letter-spacing: .1em; text-transform: uppercase; cursor: pointer; }
.admin-panels { display: grid; grid-template-columns: minmax(0, 1.08fr) minmax(0, .92fr); gap: 22px; }
.glass-panel { position: relative; border: 1px solid rgba(114, 245, 255, .28); padding: clamp(22px, 4vw, 34px); background: linear-gradient(155deg, rgba(15, 24, 54, .86), rgba(7, 11, 28, .93)); box-shadow: 0 24px 70px rgba(0, 0, 0, .35); overflow: hidden; }
.glass-panel[data-tone="pink"] { border-color: rgba(255, 47, 180, .3); }
.glass-panel::before { content: ""; position: absolute; inset: 0 auto auto 0; width: 96px; height: 3px; background: #72f5ff; box-shadow: 0 0 18px #72f5ff; }
.glass-panel[data-tone="pink"]::before { background: #ff2fb4; box-shadow: 0 0 18px #ff2fb4; }
.panel-number { color: #72f5ff; font: 900 12px var(--juliam-mono); letter-spacing: .15em; }
.glass-panel[data-tone="pink"] .panel-number { color: #ff71cf; }
.glass-panel h2 { margin: 10px 0 6px; font-family: var(--juliam-display); font-size: 28px; text-transform: uppercase; }
.panel-copy { margin: 0 0 22px; color: #99a3bf; line-height: 1.55; }
.admin-form, .key-form { display: grid; gap: 16px; }
.form-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
label { display: grid; gap: 7px; }
label > span { color: #b8c1dc; font: 800 11px var(--juliam-mono); letter-spacing: .12em; text-transform: uppercase; }
input, select { width: 100%; border: 1px solid rgba(155, 172, 213, .28); border-radius: 0; padding: 13px 14px; color: #f7f8ff; background: rgba(2, 6, 18, .7); outline: none; }
input:focus, select:focus { border-color: #72f5ff; box-shadow: 0 0 0 3px rgba(114, 245, 255, .1); }
option { color: #11182c; }
.primary-button { margin-top: 4px; color: #041017; background: #72f5ff; box-shadow: 0 0 22px rgba(114, 245, 255, .23); }
.primary-button.pink { color: #190311; border-color: #ff2fb4; background: #ff2fb4; box-shadow: 0 0 22px rgba(255, 47, 180, .23); }
.primary-button:disabled { opacity: .55; cursor: wait; }
.key-list { display: grid; gap: 10px; margin-top: 22px; }
.key-row { display: grid; grid-template-columns: 1fr auto auto; gap: 12px; align-items: center; padding: 12px; border: 1px solid rgba(255, 255, 255, .1); background: rgba(2, 6, 18, .48); }
.key-row a { display: grid; color: #f7f8ff; text-decoration: none; }
.key-row small { color: #ff71cf; font: 800 9px var(--juliam-mono); letter-spacing: .16em; }
.key-row strong { font-family: var(--juliam-mono); letter-spacing: .08em; }
.key-row > span { color: #aeb5cc; font-size: 12px; }
.key-row button { border: 0; color: #ff8eb7; background: transparent; cursor: pointer; }
.empty-state { margin: 24px 0 0; padding: 20px; border: 1px dashed rgba(255, 255, 255, .15); color: #7f89a8; text-align: center; }
.access-denied { min-height: 70vh; display: grid; place-content: center; justify-items: center; gap: 12px; text-align: center; }
.access-denied strong { color: #ff2fb4; font: 900 clamp(70px, 15vw, 140px) var(--juliam-display); text-shadow: 0 0 34px rgba(255, 47, 180, .3); }
.access-denied a { color: #72f5ff; }
@media (max-width: 820px) { .admin-panels { grid-template-columns: 1fr; } .page-head { align-items: start; flex-direction: column; } }
@media (max-width: 520px) { .form-pair { grid-template-columns: 1fr; } .key-row { grid-template-columns: 1fr auto; } .key-row button { grid-column: 1 / -1; text-align: left; padding: 0; } }
</style>
